import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { baseURL } from "../../../config/api";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import CustomFormatDatePicker from "../../../components/CustomFormatDatePicker";
import useAuth from "../../../hooks/useAuth";

export const AddFuelSurchargeRule = () => {
    const navigate = useNavigate();
    const { auth }: any = useAuth();
    const [vehicleTypeId, setVehicleTypeId] =
        useState<string>("");

    const [seasonId, setSeasonId] =
        useState<string>("");

    const [calculationType, setCalculationType] =
        useState<string>("fixed");

    const [calculationBase, setCalculationBase] =
        useState<string>("base_cost");

    const [amount, setAmount] =
        useState<string>("0");

    const [percentage, setPercentage] =
        useState<string>("0");

    const [minimumAmount, setMinimumAmount] =
        useState<string>("0");

    const [maximumAmount, setMaximumAmount] =
        useState<string>("0");

    const [priority, setPriority] =
        useState<string>("0");

    const [validFrom, setValidFrom] =
        useState<string>("");

    const [validTo, setValidTo] =
        useState<string>("");

    const [enabled, setEnabled] =
        useState<string>("No");

    const [description, setDescription] =
        useState<string>("");

    const [pleaseWaitPopup, setPleaseWaitPopup] =
        useState<boolean>(false);

    const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);

    const [seasonOptions, setSeasonOptions] = useState([]);

    useEffect(() => {
        axios.get(`${baseURL}vehicle-types/`)
            .then((response) => {
                const vehicleTypes = response.data.data || [];

                setVehicleTypeOptions(
                    vehicleTypes.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }))
                );

                setVehicleTypeId(String(vehicleTypes[0]?.id ?? "0"));
            })
            .catch((error) => {
                setVehicleTypeOptions([]);
                setVehicleTypeId("0");
                console.error(error);
            });
    }, []);

    useEffect(() => {
        axios.get(`${baseURL}seasons/`)
            .then((response) => {
                const seasons = response.data.data || [];

                setSeasonOptions(
                    seasons.map((item: any) => ({
                        value: item.id,
                        label: item.name,
                    }))
                );

                setSeasonId(String(seasons[0]?.id ?? "0"));
            })
            .catch((error) => {
                setSeasonOptions([]);
                setSeasonId("0");
                console.error(error);
            });
    }, []);

    const handleSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        const payload = {
            vehicle_type_id: Number(vehicleTypeId),
            season_id: Number(seasonId),

            calculation_type: calculationType,
            calculation_base: calculationBase,

            amount:
                calculationType === "fixed" ||
                    calculationType === "per_mile"
                    ? Number(amount)
                    : null,

            percentage:
                calculationType === "percentage"
                    ? Number(percentage)
                    : null,

            minimum_amount:
                minimumAmount !== ""
                    ? Number(minimumAmount)
                    : null,

            maximum_amount:
                maximumAmount !== ""
                    ? Number(maximumAmount)
                    : null,

            priority: Number(priority),

            valid_from: validFrom
                ? new Date(validFrom).toISOString()
                : null,

            valid_to: validTo
                ? new Date(validTo).toISOString()
                : null,

            is_active: enabled === "Yes",

            description: description.trim(),

            createdBy: auth?.givenname,
        };

        await axios
            .post(
                `${baseURL}fuel-surcharge-rules/`,
                payload,
                {
                    headers: {
                        "Content-Type":
                            "application/json"
                    },
                    withCredentials: false
                }
            )
            .then(() => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);

                console.error(
                    "Error creating fuel surcharge rule:",
                    error
                );
            });
    };


    return (
        <>
            <div className="px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto">

                <div className="w-full bg-white p-4 rounded-md">

                    <h2 className="text-lg font-semibold">
                        Add Fuel Surcharge Rule
                    </h2>

                    <div className="mt-2 p-2">

                        <form onSubmit={handleSubmit}>

                            <div className="w-full">

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Vehicle Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={vehicleTypeId}
                                            onChange={(e) => setVehicleTypeId(e.target.value)}
                                        >
                                            {vehicleTypeOptions?.map((vehicleTypes: any) => (
                                                <option key={vehicleTypes.value} value={String(vehicleTypes.value)}>
                                                    {vehicleTypes.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Season{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={seasonId}
                                            onChange={(e) => setSeasonId(e.target.value)}
                                        >
                                            {seasonOptions?.map((seasons: any) => (
                                                <option key={seasons.value} value={String(seasons.value)}>
                                                    {seasons.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Type{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationType}
                                            onChange={(e) =>
                                                setCalculationType(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="fixed">
                                                Fixed
                                            </option>

                                            <option value="percentage">
                                                Percentage
                                            </option>

                                            <option value="per_mile">
                                                Per Mile
                                            </option>
                                        </select>
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Calculation Base{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={calculationBase}
                                            onChange={(e) =>
                                                setCalculationBase(
                                                    e.target.value
                                                )
                                            }
                                            required
                                        >
                                            <option value="base_cost">
                                                Base Cost
                                            </option>

                                            <option value="duration_cost">
                                                Duration Cost
                                            </option>

                                            <option value="mileage_cost">
                                                Mileage Cost
                                            </option>

                                            <option value="selected_base_cost">
                                                Selected Base Cost
                                            </option>

                                            <option value="subtotal">
                                                Subtotal
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    {(
                                        calculationType === "fixed" ||
                                        calculationType === "per_mile"
                                    ) && (
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                    {calculationType === "per_mile"
                                                        ? "Amount Per Mile"
                                                        : "Amount"}

                                                    <span className="text-red-500">
                                                        {" "}*
                                                    </span>
                                                </label>

                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="any"
                                                    className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                    value={amount}
                                                    onChange={(e) =>
                                                        setAmount(e.target.value)
                                                    }
                                                    required
                                                />
                                            </div>
                                        )}


                                    {calculationType === "percentage" && (
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                                Percentage
                                                <span className="text-red-500">
                                                    {" "}*
                                                </span>
                                            </label>

                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="any"
                                                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                value={percentage}
                                                onChange={(e) =>
                                                    setPercentage(e.target.value)
                                                }
                                                required
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Priority
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={priority}
                                            onChange={(e) =>
                                                setPriority(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Minimum Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={minimumAmount}
                                            onChange={(e) =>
                                                setMinimumAmount(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Maximum Amount
                                        </label>

                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={maximumAmount}
                                            onChange={(e) =>
                                                setMaximumAmount(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid From
                                        </label>

                                        <CustomFormatDatePicker
                                            value={validFrom}
                                            onChange={(value) => {
                                                setValidFrom(value);
                                            }}
                                            required={true}
                                        />
                                    </div>


                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Valid To
                                        </label>

                                        <CustomFormatDatePicker
                                            minDate={
                                                validFrom ||
                                                undefined
                                            }
                                            value={validTo}
                                            onChange={(value) => {
                                                setValidTo(value);
                                            }}
                                            required={true}
                                        />
                                    </div>

                                </div>


                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                            Active
                                        </label>

                                        <select
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={enabled}
                                            onChange={(e) =>
                                                setEnabled(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="No">
                                                No
                                            </option>

                                            <option value="Yes">
                                                Yes
                                            </option>
                                        </select>
                                    </div>

                                </div>


                                <div className="mb-4">

                                    <label className="block text-sm font-bold text-gray-700 mb-2 mt-2">
                                        Description
                                    </label>

                                    <textarea
                                        rows={4}
                                        value={description}
                                        onChange={(e) =>
                                            setDescription(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Enter fuel surcharge rule description"
                                        className="w-full p-4 border border-gray-300 rounded-md bg-white resize-none outline-none focus:border-primary"
                                    />

                                </div>


                                <div className="flex flex-col sm:flex-row gap-4 mt-6">

                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(-1)
                                        }
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-white text-primary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>
                </div>
            </div>

            <PleasewaitPopup
                showPopup={pleaseWaitPopup}
            />
        </>
    );
};